import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';

import Kpi from 'types/Kpi';
import KpiData, { EmptyKpiData } from 'types/KpiData';

import { HIGH_MONTH_SENTINEL, LOW_MONTH_SENTINEL, Month } from '../types';
import { compareMonths, getMonthsBetweenRange, incrementMonth } from '../utils';

interface Params {
    kpis: Kpi[];
    setKpis: Dispatch<SetStateAction<Kpi[]>>;
    firstDataMonthLimit?: Month;
}

const extractMonthFromKpiData = ({ month, year }: KpiData) => {
    return { month, year };
};

const useKpiTabLogic = (params: Params) => {
    const { kpis, setKpis, firstDataMonthLimit } = params;

    const hasBimonthly = useMemo(() => {
        return kpis.some(k => k.monitoringFrequency === 'bimonthly');
    }, [kpis]);

    const hasQuarterly = useMemo(() => {
        return kpis.some(k => k.monitoringFrequency === 'quarterly');
    }, [kpis]);

    const [baseFirstMonth, baseLastMonth] = useMemo(() => {
        return kpis.reduce((result, kpi) => {
            return kpi.data.reduce((internalResult, kpiData) => {
                let [internalFirst, internalLast] = internalResult;

                if (compareMonths(kpiData, internalFirst) < 0) {
                    internalFirst = extractMonthFromKpiData(kpiData);
                }

                if (compareMonths(kpiData, internalLast) > 0) {
                    internalLast = extractMonthFromKpiData(kpiData);
                }

                return [internalFirst, internalLast];
            }, result);
        }, [LOW_MONTH_SENTINEL, HIGH_MONTH_SENTINEL]);
    }, [kpis]);

    const [firstMonth, lastMonth] = useMemo(() => {
        const clonedFirstMonth = { ...baseFirstMonth };
        const clonedLastMonth = { ...baseLastMonth };

        while (hasBimonthly && (clonedFirstMonth.month - 1) % 2 !== 0) {
            clonedFirstMonth.month--;
        }

        while (hasQuarterly && (clonedFirstMonth.month - 1) % 3 !== 0) {
            clonedFirstMonth.month--;
        }

        while (hasBimonthly && clonedLastMonth.month % 2 !== 0) {
            clonedLastMonth.month++;
        }

        while (hasQuarterly && clonedLastMonth.month % 3 !== 0) {
            clonedLastMonth.month++;
        }

        return [clonedFirstMonth, clonedLastMonth];
    }, [baseFirstMonth, baseLastMonth, hasBimonthly, hasQuarterly]);

    const months: Month[] = useMemo(() => {
        return getMonthsBetweenRange(firstMonth, lastMonth);
    }, [firstMonth, lastMonth]);

    const canAddNewMonth = useMemo(() => {
        if (!firstDataMonthLimit) return false;

        return compareMonths(firstDataMonthLimit, firstMonth) < 0;
    }, [firstMonth, firstDataMonthLimit]);

    const addMonth = useCallback(() => {
        const newMonth = incrementMonth(firstMonth, -1);

        setKpis(data => {
            const firstKpi = { ...data[0] };

            firstKpi.data = [...firstKpi.data, {
                ...EmptyKpiData,
                ...newMonth,
                amount: '', // TODO: is this needed? why not leave it null?
                kpiId: firstKpi.id,
            }];

            return [firstKpi, ...data.slice(1)];
        });
    }, [firstMonth, setKpis]);

    return { months, canAddNewMonth, addMonth };

};

export default useKpiTabLogic;